import { userLogin } from '@/app/actions/auth/actions'
import { BRANDSIDE_SERVICE_URL } from '@/config'
import { apiService } from '@/services/Api'
import { marketingStorage } from '@/store/MarketingStorage'
import {
  instanceOfAuthTokenResponse,
  Login200Response,
  Unauthorized,
} from '@/services/Api/generated'
import { Validator } from '@/shared/Auth/utils/Validator'
import { ERRORS_CODE } from '@/shared/Auth/utils/Validator/errors'
import { prepareApiErrors } from '@/shared/Auth/utils/Validator/validate'
import { getPhoneNumber } from '@/utils/string'
import {
  acceptPolicyValidator,
  confirmCodeValidator,
  emailValidator,
  passwordValidator,
  phoneValidator,
  requiredFieldValidator,
  retryPasswordValidator,
} from '@/utils/validator'
import { makeAutoObservable, reaction, runInAction } from 'mobx'
import * as Yup from 'yup'
import { popupStore, PopupStore } from '../Popup'

export const isAuthTokenResponseSuccess = (
  response: Login200Response | Unauthorized
): response is Login200Response => {
  return (
    response.status === true &&
    'data' in response &&
    response.data != null &&
    instanceOfAuthTokenResponse(response.data)
  )
}

export enum SCREEN_LIST {
  LOGIN = 'LOGIN',
  REGISTRATION = 'REGISTRATION',
  REGISTRATION_SUCCESS = 'REGISTRATION_SUCCESS',
  EMAIL_NOT_VERIFIED = 'EMAIL_NOT_VERIFIED',
  RESET_PASSWORD = 'RESET_PASSWORD',
  PASSWORD_RESET_REQUEST = 'PASSWORD_RESET_REQUEST',
  CONFIRM_RESET_PASSWORD = 'CONFIRM_RESET_PASSWORD',
  PASSWORD_RESET = 'PASSWORD_RESET',
}

export const TabsOptions = [
  { value: SCREEN_LIST.LOGIN, label: 'Вход' },
  { value: SCREEN_LIST.REGISTRATION, label: 'Регистрация' },
]

export function isScreenList(name: string): name is SCREEN_LIST {
  return name in SCREEN_LIST
}

const registrationFormSchema = Yup.object().shape({
  name: requiredFieldValidator,
  phone: phoneValidator,
  email: emailValidator,
  password: passwordValidator,
  acceptPolicy: acceptPolicyValidator,
})

const loginFormSchema = Yup.object().shape({
  email: emailValidator,
  password: requiredFieldValidator,
})

const passwordResetRequestFormSchema = Yup.object().shape({
  email: emailValidator,
})

const confirmResetPasswordFormSchema = Yup.object().shape({
  code: confirmCodeValidator,
})

const resetPasswordFormSchema = Yup.object().shape({
  newPassword: passwordValidator,
  retryNewPassword: retryPasswordValidator('newPassword'),
})

export class AuthStore {
  form = {
    name: '',
    phone: '',
    email: '',
    password: '',
    acceptPolicy: false,
  }

  loading: boolean = false

  confirmResetPasswordCode: string = ''
  confirmResetPasswordError: boolean = false
  confirmResetToken: string = ''

  screen: SCREEN_LIST = SCREEN_LIST.LOGIN

  newPassword: string = ''
  retryNewPassword: string = ''

  loadingPage: boolean = false

  private validators: { [key in SCREEN_LIST]?: Validator } = {
    [SCREEN_LIST.REGISTRATION]: new Validator(registrationFormSchema),
    [SCREEN_LIST.LOGIN]: new Validator(loginFormSchema),
    [SCREEN_LIST.PASSWORD_RESET_REQUEST]: new Validator(
      passwordResetRequestFormSchema
    ),
    [SCREEN_LIST.CONFIRM_RESET_PASSWORD]: new Validator(
      confirmResetPasswordFormSchema
    ),
    [SCREEN_LIST.PASSWORD_RESET]: new Validator(resetPasswordFormSchema),
  }

  get validator() {
    return this.validators[this.screen]
  }

  constructor(private _popupStore: PopupStore) {
    makeAutoObservable(this, {}, { autoBind: true })
    reaction(
      () => this.screen,
      () => this.validator?.validateFilled(this.form)
    )
  }

  async registration() {
    if (!this.validator?.validateAll(this.form)) {
      return { status: false }
    }

    this.startLoading()
    try {
      const phone = getPhoneNumber(this.form.phone)
      if (phone == null) {
        return { status: false }
      }
      const response = await apiService.user.registration({
        registrationRequest: {
          email: this.form.email,
          name: this.form.name,
          password: this.form.password,
          phone,
          utmClientId: marketingStorage.getClientId() ?? undefined,
          promocode: marketingStorage.getPromocode() ?? undefined,
        },
      })

      if (response.status) {
        marketingStorage.clearClientId()
        marketingStorage.clearPromocode()
        this.setLoadingPage(true)
        this.setScreen(SCREEN_LIST.REGISTRATION_SUCCESS)
        this.setLoadingPage(false)
      }
    } catch (error: any) {
      const response = await error.response.json()
      const errors = response?.errors ?? {}
      this.validator.setErrors(errors)
      return error
    } finally {
      this.stopLoading()
    }

    return { status: false }
  }

  async login() {
    let result = false
    if (this.validator?.validateAll(this.form)) {
      this.startLoading()
      try {
        const utmClientId = marketingStorage.getClientId()
        const promocode = marketingStorage.getPromocode()

        const response = await userLogin(
          this.form.email,
          this.form.password,
          utmClientId,
          promocode
        )

        if (isAuthTokenResponseSuccess(response)) {
          marketingStorage.clearClientId()
          marketingStorage.clearPromocode()
          if (BRANDSIDE_SERVICE_URL != null && response.data != null) {
            this.goToService(
              response.data.accessToken,
              response.data.refreshToken,
              response.data.expiresIn
            )
          }
          result = true
        } else {
          result = false
          const errors = response.errors
          if (errors != null) {
            if (errors._common === ERRORS_CODE.EMAIL_NOT_VERIFIED) {
              this.setScreen(SCREEN_LIST.EMAIL_NOT_VERIFIED)
            }
            this.validator.setErrors(errors)
          }
        }
      } catch (error) {
      } finally {
        this.stopLoading()
      }
    }
    return { status: result }
  }

  goToService(accessToken: string, refreshToken: string, expiresIn: number) {
    const url = `${BRANDSIDE_SERVICE_URL}/auth/site/?accessToken=${accessToken}&refreshToken=${refreshToken}&expiresIn=${expiresIn}`

    const newWindow = window.open(url, '_blank')

    if (!newWindow) {
      window.location.href = url
    }
  }

  resetPassword() {
    if (this.validator?.validateAll(this.form)) {
      // this._popupStore.hidePopup()
    }
  }

  async passwordResetRequest() {
    if (this.validator?.validateAll(this.form)) {
      this.startLoading()
      try {
        const response = await apiService.user.forgetPassword({
          email: this.form.email,
        })
        if (response.status) {
          this.setScreen(SCREEN_LIST.CONFIRM_RESET_PASSWORD)
        }
      } catch (error: any) {
        this.validator.setErrors(prepareApiErrors(error.response.errors))
      } finally {
        this.stopLoading()
      }
    }
  }

  async setConfirmResetPasswordCode(value: string) {
    this.confirmResetPasswordCode = value

    if (value.length !== 6) {
      return
    }

    try {
      this.startLoading()
      const response = await apiService.user.resetPasswordToken({
        code: value,
      })
      if (response.status) {
        this.confirmResetToken = response.data.token
        this.setScreen(SCREEN_LIST.PASSWORD_RESET)
      }
    } catch (error) {
      this.confirmResetPasswordError = true
    } finally {
      this.stopLoading()
    }
  }

  async saveNewPassword() {
    if (
      this.validator?.validateAll({
        newPassword: this.newPassword,
        retryNewPassword: this.retryNewPassword,
      })
    ) {
      this.startLoading()
      const response = await apiService.user.newPassword({
        token: this.confirmResetToken,
        password: this.newPassword,
        passwordConfirmation: this.retryNewPassword,
      })

      this.setPassword('')

      runInAction(() => {
        this.confirmResetPasswordCode = ''
      })

      this.validators?.[SCREEN_LIST.LOGIN]?.setErrors({ _common: null })

      this.stopLoading()
      return response
    } else {
      return { status: false }
    }
  }

  goToLogin() {
    this.screen = SCREEN_LIST.LOGIN
  }

  goToResetPassword() {
    this.screen = SCREEN_LIST.PASSWORD_RESET_REQUEST
  }

  goToRegistration() {
    this.screen = SCREEN_LIST.REGISTRATION
  }

  setScreen(screen: SCREEN_LIST) {
    this.screen = screen
  }

  setLoadingPage(value: boolean) {
    this.loadingPage = value
  }

  startLoading() {
    this.loading = true
  }

  stopLoading() {
    this.loading = false
  }

  setName(name: string) {
    this.validator?.validateAt('name', name)
    this.form.name = name
  }

  setPhone(phone: string) {
    this.validator?.validateAt('phone', phone)
    this.form.phone = phone
  }

  setEmail(email: string) {
    this.validator?.validateAt('email', email)
    this.form.email = email
  }

  setPassword(password: string) {
    this.validator?.validateAt('password', password)
    this.form.password = password
  }

  setAcceptPolicy(acceptPolicy: boolean) {
    this.validator?.validateAt('acceptPolicy', acceptPolicy)
    this.form.acceptPolicy = acceptPolicy
  }

  setNewPassword(value: string) {
    this.newPassword = value
  }

  setRetryNewPassword(value: string) {
    this.retryNewPassword = value
  }

  resetData() {
    this.form = {
      name: '',
      phone: '',
      email: '',
      password: '',
      acceptPolicy: false,
    }

    this.loading = false

    this.confirmResetPasswordCode = ''
    this.confirmResetPasswordError = false
    this.confirmResetToken = ''

    this.screen = SCREEN_LIST.LOGIN

    this.newPassword = ''
    this.retryNewPassword = ''
  }

  async checkAuth() {
    const newWindow = window.open(
      `${BRANDSIDE_SERVICE_URL}/auth/site/get-token?siteOrigin=${window.location.origin}`,
      '_blank'
    )

    if (newWindow != null) {
      const isAuth = await new Promise<string>((resolve) => {
        const eventHandler = (event: any) => {
          if (event.origin === BRANDSIDE_SERVICE_URL) {
            window.removeEventListener('message', eventHandler)
            resolve(event.data.isAuth)
          }
        }

        window.addEventListener('message', eventHandler)
      })

      if (!isAuth) {
        newWindow.close()
      }

      return isAuth
    }
  }
}

export const authStore = new AuthStore(popupStore)
